import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import JsonLd from '@/components/base/JsonLd';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://oopbuyspread.pics';
const LOGO_URL = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg';

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'OOPBuy Versand Nach Deutschland: Der Vollständige Guide 2026',
  description: 'Versandoptionen, Lieferzeiten, Zollgebühren und Einfuhrumsatzsteuer. Alles was deutsche Käufer über den internationalen Versand mit dem OOPBuy Spreadsheet wissen müssen.',
  author: {
    '@type': 'Organization',
    name: 'OOPBuy Editorial Team',
    url: SITE_URL,
  },
  publisher: {
    '@type': 'Organization',
    name: 'OOPBuy Spreadsheet',
    url: SITE_URL,
    logo: { '@type': 'ImageObject', url: LOGO_URL },
  },
  datePublished: '2026-07-15',
  dateModified: '2026-07-15',
  mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/artikel/oopbuy-versand` },
  inLanguage: 'de',
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { '@type': 'Question', name: 'Wie lange dauert der Versand nach Deutschland wirklich?', acceptedAnswer: { '@type': 'Answer', text: 'Standardversand dauert 10-20 Werktage. Express 5-8 Werktage. Diese Angaben sind im OOPBuy Spreadsheet für jedes Produkt individuell hinterlegt.' } },
    { '@type': 'Question', name: 'Muss ich immer Zoll bezahlen?', acceptedAnswer: { '@type': 'Answer', text: 'Bei Warenwerten unter 150 € fällt kein Zoll an, nur die Einfuhrumsatzsteuer von 19%.' } },
    { '@type': 'Question', name: 'Kann ich mehrere Bestellungen zusammenlegen?', acceptedAnswer: { '@type': 'Answer', text: 'Ja, das Spreadsheet unterstützt Sammellieferungen. Du sparst Versandkosten und reduzierst den Verwaltungsaufwand.' } },
    { '@type': 'Question', name: 'Was passiert bei Verlust oder Beschädigung?', acceptedAnswer: { '@type': 'Answer', text: 'Alle Versandoptionen im Spreadsheet sind versichert. Bei Verlust oder Beschädigung greift die Buyer Protection.' } },
  ],
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Startseite', item: SITE_URL },
    { '@type': 'ListItem', position: 2, name: 'OOPBuy Versandguide', item: `${SITE_URL}/artikel/oopbuy-versand` },
  ],
};

export default function VersandPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const currentDate = '2026-07-15';

  return (
    <>
      <Navbar />
      <JsonLd data={articleJsonLd} />
      <JsonLd data={faqJsonLd} />
      <JsonLd data={breadcrumbJsonLd} />
      <article className="min-h-screen bg-background-50 mt-16 md:mt-[4.5rem]">
        <header className="w-full bg-background-100 py-16 md:py-20">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 font-[family-name:var(--font-label)]" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-foreground-800 transition-colors cursor-pointer">Startseite</Link>
              <i className="ri-arrow-right-s-line text-sm"></i>
              <span className="text-foreground-800">OOPBuy Versandguide</span>
            </nav>
            <p className="text-xs uppercase tracking-[0.2em] text-primary-600 mb-3 font-[family-name:var(--font-label)]">Versand & Logistik</p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-6">
              OOPBuy Versand Nach Deutschland: Der Vollständige Guide 2026
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl font-[family-name:var(--font-body)]">
              Versandoptionen, Lieferzeiten, Zollgebühren und Einfuhrumsatzsteuer. Alles was deutsche Käufer über den internationalen Versand mit dem OOPBuy Spreadsheet wissen müssen.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-6 pt-6 border-t border-background-200">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-sm text-primary-600"></i>
                </div>
                <span className="text-xs text-foreground-600 font-[family-name:var(--font-label)]">OOPBuy Editorial Team</span>
              </div>
              <span className="text-xs text-foreground-500 font-[family-name:var(--font-label)]">Veröffentlicht: {currentDate}</span>
              <span className="text-xs text-foreground-500 font-[family-name:var(--font-label)]">Lesezeit: 11 Minuten</span>
            </div>
          </div>
        </header>

        <nav className="w-full max-w-3xl mx-auto px-4 md:px-6 py-8" aria-label="Inhaltsverzeichnis">
          <div className="bg-background-100 rounded-xl p-6">
            <h2 className="text-sm font-semibold text-foreground-800 mb-4 font-[family-name:var(--font-label)]">Inhaltsverzeichnis</h2>
            <ul className="space-y-2">
              {tocLabels.map((label, i) => (
                <li key={tocIds[i]}><a href={`#${tocIds[i]}`} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer font-[family-name:var(--font-body)]">{i + 1}. {label}</a></li>
              ))}
            </ul>
          </div>
        </nav>

        <div className="w-full max-w-3xl mx-auto px-4 md:px-6 pb-16 md:pb-20">
          <div className="prose-custom space-y-8 text-sm md:text-base text-foreground-700 leading-relaxed font-[family-name:var(--font-body)]">

            <section id="einleitung">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">Einleitung: Versand Mit Dem OOPBuy Spreadsheet</h2>
              <p>Der Versand nach Deutschland ist für viele internationale Shopping-Neulinge die größte Hürde. Das <strong>OOPBuy Spreadsheet</strong> nimmt dir diese Sorge ab. Wir haben alle Versandinformationen speziell für den deutschen Markt aufbereitet - von Lieferzeiten über Zollgebühren bis hin zu Verpackungsoptionen.</p>
              <p>In diesem umfassenden Versandguide erklären wir dir Schritt für Schritt, wie die Logistik hinter dem <strong>OOPBuy Spreadsheet Deutschland</strong> funktioniert und worauf du achten musst, damit deine Bestellung sicher und pünktlich bei dir ankommt.</p>
            </section>

            <section id="versandoptionen">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">Versandoptionen Im Überblick</h2>
              <p>Das <strong>OOPBuy Spreadsheet</strong> bietet verschiedene Versandoptionen, die je nach Produktkategorie und Dringlichkeit variieren. Jede Option ist im Spreadsheet klar gekennzeichnet.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 my-6">
                {shippingOptions.map((opt, i) => (
                  <div key={i} className="p-5 rounded-xl bg-background-100 text-center hover:bg-background-200 transition-colors">
                    <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                      <i className={`${opt.icon} text-3xl text-primary-500`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-2 font-[family-name:var(--font-label)]">{opt.name}</h3>
                    <p className="text-xs text-foreground-500 mb-2">{opt.time}</p>
                    <span className="text-sm font-bold text-primary-600 font-[family-name:var(--font-label)]">{opt.price}</span>
                  </div>
                ))}
              </div>
            </section>

            <section id="lieferzeiten">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">Lieferzeiten Nach Deutschland</h2>
              <p>Die Lieferzeiten im <strong>OOPBuy Spreadsheet</strong> hängen von mehreren Faktoren ab: der gewählten Versandoption, dem Herkunftsland des Verkäufers und der aktuellen Auslastung der Logistikzentren. Im Durchschnitt kannst du mit folgenden Zeiten rechnen:</p>
              <ul className="space-y-3 my-6 list-none">
                {deliveryTimes.map((dt, i) => (
                  <li key={i} className="flex items-center gap-3 p-3 rounded-lg bg-background-100">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-primary-600 text-sm"></i>
                    </div>
                    <span className="text-sm text-foreground-700 font-[family-name:var(--font-body)]"><strong className="text-foreground-900">{dt.method}:</strong> {dt.time}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section id="zoll">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">Zollgebühren Und Einfuhrumsatzsteuer</h2>
              <p>Ein entscheidender Vorteil des <strong>OOPBuy Spreadsheet</strong> ist die transparente Darstellung von Zollgebühren und Einfuhrumsatzsteuer. Anders als bei vielen anderen Agenten zeigen wir dir vor dem Kauf genau, welche zusätzlichen Kosten auf dich zukommen.</p>
              <div className="my-6 p-5 rounded-xl bg-primary-50 border border-primary-200">
                <h3 className="text-sm font-semibold text-primary-800 mb-3 font-[family-name:var(--font-label)]">Wichtige Zoll-Freigrenzen 2026:</h3>
                <ul className="space-y-2">
                  {zollInfo.map((z, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                      <span>{z}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section id="tracking">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">Sendungsverfolgung Mit Dem OOPBuy Spreadsheet</h2>
              <p>Jede Bestellung über das <strong>OOPBuy Spreadsheet</strong> erhält eine Tracking-Nummer, mit der du den Status deiner Lieferung in Echtzeit verfolgen kannst. Das Spreadsheet integriert Tracking-Informationen direkt in die Bestellübersicht, sodass du nie den Überblick verlierst.</p>
              <p>Von der Abholung im Lager des Verkäufers über den internationalen Transport bis zur Zustellung durch DHL oder Hermes - du siehst jeden Schritt. Das <strong>OOPBuy Spreadsheet Deutschland</strong> System benachrichtigt dich automatisch bei Statusänderungen.</p>
            </section>

            <section id="verpackung">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">Verpackung Und Zusätzliche Services</h2>
              <p>Das <strong>OOPBuy Spreadsheet</strong> bietet verschiedene Verpackungsoptionen, die du je nach Produkt wählen kannst. Für empfindliche Artikel wie Sneaker gibt es spezielle Schutzverpackungen. Bei mehreren Artikeln kannst du eine Sammellieferung wählen, um Versandkosten zu sparen.</p>
            </section>

            <section id="faq">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">Häufig Gestellte Fragen Zum Versand</h2>
              <div className="space-y-4">
                {faqItems.map((faq, i) => (
                  <details key={i} className="group bg-background-100 rounded-xl overflow-hidden">
                    <summary className="flex items-center justify-between p-4 cursor-pointer font-[family-name:var(--font-label)]">
                      <span className="text-sm font-semibold text-foreground-800 pr-4">{faq.q}</span>
                      <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                    </summary>
                    <div className="px-4 pb-4"><p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p></div>
                  </details>
                ))}
              </div>
            </section>

            <section id="fazit">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-12 mb-4">Fazit: Versand Leicht Gemacht Mit OOPBuy</h2>
              <p>Der Versand nach Deutschland muss keine Hürde sein. Mit dem <strong>OOPBuy Spreadsheet</strong> hast du alle Informationen an einem Ort: Versandoptionen, Lieferzeiten, Zollgebühren und Tracking. Kein lästiges Suchen, keine bösen Überraschungen - einfach sorgenfrei bestellen.</p>
              <p>Nutze die integrierten Versandinformationen in unserem <strong>Spreadsheet</strong> und wähle die Option, die am besten zu deinem Budget und deiner Geduld passt. Bei Fragen hilft dir unser Support-Team gerne weiter.</p>
            </section>

            <div className="mt-12 p-8 rounded-2xl bg-primary-500 text-center">
              <h2 className="text-xl font-heading font-bold text-background-50 mb-3">Bereit Für Deine Erste Bestellung?</h2>
              <p className="text-sm text-primary-100 mb-6 font-[family-name:var(--font-body)]">Nutze das OOPBuy Spreadsheet mit optimierten Versandoptionen für Deutschland.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center px-8 py-3.5 rounded-full bg-background-50 text-primary-600 text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-background-100 transition-colors font-[family-name:var(--font-label)]">Buy It Now<i className="ri-arrow-right-line ml-2"></i></a>
                <Link to="/" className="inline-flex items-center px-6 py-3.5 rounded-full border border-primary-300 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-colors font-[family-name:var(--font-label)]">Zurück Zur Startseite</Link>
              </div>
            </div>

            <div className="mt-12 p-6 rounded-xl bg-background-100">
              <p className="text-xs text-foreground-500 mb-3 font-[family-name:var(--font-label)]">Das könnte dich auch interessieren:</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer font-[family-name:var(--font-body)]">← OOPBuy Spreadsheet Startseite</Link>
                <Link to="/artikel/oopbuy-sicherheit" className="text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer font-[family-name:var(--font-body)]">Sicherheitsguide</Link>
                <Link to="/artikel/oopbuy-vergleich" className="text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer font-[family-name:var(--font-body)]">OOPBuy vs Alternativen</Link>
              </div>
            </div>

            <div className="mt-8 p-4 rounded-lg border border-background-200">
              <p className="text-xs text-foreground-400 leading-relaxed font-[family-name:var(--font-body)]">
                <strong>Haftungsausschluss:</strong> Versandzeiten und Zollgebühren können je nach Saison und politischen Entwicklungen variieren. Die Angaben basieren auf Erfahrungswerten vom Juli 2026.
              </p>
            </div>
          </div>
        </div>

        <footer className="w-full bg-primary-900">
          <div className="w-full max-w-6xl mx-auto px-4 md:px-6 py-12">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <Link to="/" className="text-sm text-foreground-300 hover:text-background-50 transition-colors cursor-pointer font-[family-name:var(--font-body)]">← Zurück zur OOPBuy Spreadsheet Startseite</Link>
              <p className="text-xs text-foreground-400 font-[family-name:var(--font-body)]">&copy; {new Date().getFullYear()} OOPBuy Spreadsheet. Alle Rechte vorbehalten.</p>
            </div>
          </div>
        </footer>
      </article>
    </>
  );
}

const tocIds = ['einleitung','versandoptionen','lieferzeiten','zoll','tracking','verpackung','faq','fazit'];
const tocLabels = ['Einleitung','Versandoptionen','Lieferzeiten','Zoll & Einfuhrumsatzsteuer','Sendungsverfolgung','Verpackung & Services','FAQ','Fazit'];

const shippingOptions = [
  { name: 'Standard', time: '10-20 Werktage', price: 'Ab 8,99 €', icon: 'ri-ship-line' },
  { name: 'Express', time: '5-8 Werktage', price: 'Ab 19,99 €', icon: 'ri-flight-takeoff-line' },
  { name: 'Premium', time: '3-5 Werktage', price: 'Ab 34,99 €', icon: 'ri-rocket-line' },
];

const deliveryTimes = [
  { method: 'Standardversand', time: '10-20 Werktage - Ideal für nicht eilige Bestellungen' },
  { method: 'Expressversand', time: '5-8 Werktage - Empfohlen für die meisten Bestellungen' },
  { method: 'Premiumversand', time: '3-5 Werktage - Für zeitkritische Lieferungen' },
];

const zollInfo = [
  'Warenwert unter 150 €: Keine Zollgebühren, nur Einfuhrumsatzsteuer (19%)',
  'Warenwert über 150 €: Zollgebühren plus Einfuhrumsatzsteuer',
  'Geschenke bis 45 € sind zollfrei',
  'Das Spreadsheet berechnet die voraussichtlichen Kosten automatisch',
];

const faqItems = [
  { q: 'Wie lange dauert der Versand nach Deutschland wirklich?', a: 'Standardversand dauert 10-20 Werktage. Express 5-8 Werktage. Diese Angaben sind im OOPBuy Spreadsheet für jedes Produkt individuell hinterlegt und basieren auf echten Erfahrungswerten.' },
  { q: 'Muss ich immer Zoll bezahlen?', a: 'Bei Warenwerten unter 150 € fällt kein Zoll an, nur die Einfuhrumsatzsteuer von 19%. Das Spreadsheet zeigt dir die voraussichtlichen Kosten vor dem Kauf an.' },
  { q: 'Kann ich mehrere Bestellungen zusammenlegen?', a: 'Ja, das Spreadsheet unterstützt Sammellieferungen. Du sparst Versandkosten und reduzierst den Verwaltungsaufwand.' },
  { q: 'Was passiert bei Verlust oder Beschädigung?', a: 'Alle Versandoptionen im Spreadsheet sind versichert. Bei Verlust oder Beschädigung greift die Buyer Protection und du erhältst eine Rückerstattung.' },
];